import { startTransition, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RenderJobList } from '@/components/app/RenderJobList';
import { listOrganizationRenders } from '@/services/renders';
import type { RenderJobSummary } from '@/types/api';
import { Button } from '@/components/primitives/Button';

export default function RendersPage() {
  const { renderId: renderIdParam } = useParams<{ renderId?: string }>();
  const highlightRenderId =
    renderIdParam != null ? Number.parseInt(renderIdParam, 10) : Number.NaN;
  const [rows, setRows] = useState<RenderJobSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    startTransition(() => {
      setLoading(true);
      setError('');
    });
    listOrganizationRenders({ limit: 200 })
      .then((page) => setRows(page.items))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load renders'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="aspectly:p-4 aspectly:md:p-8 aspectly:lg:p-12 aspectly:space-y-10">
      {/* Header */}
      <div className="aspectly:flex aspectly:items-end aspectly:justify-between aspectly:gap-6 aspectly:flex-wrap">
        <div className="aspectly:space-y-1">
          <h1 className="aspectly:text-4xl aspectly:font-black aspectly:tracking-tight aspectly:text-on-surface aspectly:font-headline">
            Renders
          </h1>
          <p className="aspectly:text-on-surface-variant aspectly:font-medium">
            Video export jobs for your organization. Download MP4s when status is completed.
          </p>
        </div>
        <Button onClick={() => load()} variant="outline" className="aspectly:bg-surface-container-lowest">
          <span className="material-symbols-outlined aspectly:text-[18px]">refresh</span>
          Refresh
        </Button>
      </div>

      {error ? (
        <div className="aspectly:mb-8 aspectly:p-4 aspectly:rounded-xl aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm aspectly:font-medium">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="aspectly:flex aspectly:items-center aspectly:justify-center aspectly:py-20">
          <span className="material-symbols-outlined aspectly:animate-spin aspectly:text-4xl aspectly:text-primary">
            progress_activity
          </span>
        </div>
      ) : (
        <RenderJobList
          rows={rows}
          showCreativeColumn
          highlightRenderId={Number.isNaN(highlightRenderId) ? null : highlightRenderId}
          renderLinkMode="org"
          emptyMessage="No renders have been generated yet."
        />
      )}
    </div>
  );
}
